import React, { forwardRef, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, {
  AutocompleteChangeReason,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import { SxProps } from '@mui/material';
import { Theme } from '@emotion/react';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';
import { FieldValues, Path } from 'react-hook-form';

interface Props<T extends FieldValues> {
  clear?: Boolean;
  disabled?: boolean;
  initialfilter?: string;
  initialItems?: IdNameDto[];
  label: string;
  name?: Path<T>; //name of the item within the object React Hook Forms
  newText?: string;
  required?: Boolean; //if this field is required when using React hook forms
  sx?: SxProps<Theme>;
  updatedItem?: IdNameDto;
  updatedValue?: string;
  selectedId?: string;
  add?(name: string): void;
  cleared?(): void;
  itemSelected?(item: IdNameDto | undefined): void;
  fetchData?: (input: string) => Promise<any[]>;
}

const filter = createFilterOptions<IdNameDto>();

const GenericAutoComplete = forwardRef<HTMLDivElement, Props<any>>(
  function AutoComplete<T extends FieldValues>(
    {
      clear,
      disabled,
      // control,
      initialfilter,
      initialItems,
      label,
      newText,
      sx,
      updatedItem,
      updatedValue,
      selectedId,
      add,
      cleared,
      fetchData,
      itemSelected,
    }: Props<T>,
    ref: React.ForwardedRef<HTMLDivElement>
  ) {
    const [items, setItems] = useState<IdNameDto[]>([]);
    const [selectedItem, setSelectedItem] = useState<IdNameDto | string>('');
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isOptionSelected, setIsOptionSelected] = useState(false);

    const handleOpen = () => {
      setOpen(true);
    };

    useEffect(() => {
      if (updatedValue) {
        setInputValue(updatedValue);
      }
    }, [updatedValue]);

    useEffect(() => {
      if (initialfilter !== undefined && fetchData) {
        fetchData(initialfilter).then((result) => {
          setItems(result);
        });
      }
    }, [fetchData, initialfilter]);

    useEffect(() => {
      if (initialItems) {
        setInputValue('');
        setItems(initialItems);
      }
    }, [initialItems]);

    useEffect(() => {
      if (updatedItem) {
        setSelectedItem(updatedItem);
      }
    }, [updatedItem]);

    useEffect(() => {
      if (selectedId && items) {
        const foundItem = items.find((i) => i.id === selectedId);
        if (foundItem) {
          setSelectedItem(foundItem);
        }
      } else {
        setSelectedItem('');
      }
    }, [selectedId, items]);

    useEffect(() => {
      if (clear && cleared) {
        cleared();
      }
    }, [clear, cleared]);

    useEffect(() => {
      if (!isOptionSelected && fetchData) {
        if (inputValue && inputValue.length > 1) {
          fetchData(inputValue).then((result) => {
            setItems(result);
          });
        } else {
          setItems([]);
        }
      }
    }, [fetchData, inputValue, isOptionSelected]);

    const handleClose = () => {
      setOpen(false);
    };

    const handleChange = (
      _: React.SyntheticEvent,
      data: any | null,
      reason: AutocompleteChangeReason
    ) => {
      if (data && !data.id && data.name) {
        if (data.name === newText) {
          if (add) {
            add('');
            setInputValue('');
            setSelectedItem('');
            setIsOptionSelected(false);
            setOpen(false);
          }
        } else {
          const nameToAdd = data.name.replace('Add "', '').replace('"', '');
          if (add) {
            add(nameToAdd);
            setOpen(false);
          }
          data.name = nameToAdd;
          setSelectedItem(data);
          setInputValue(nameToAdd);
        }
      } else if (reason === 'clear') {
        clearItems();
      } else if (data && data.id) {
        if (itemSelected) {
          itemSelected(data);
        }
      } else {
        if (updatedItem && itemSelected) {
          itemSelected(undefined);
        }
        setOpen(true);
      }
      setIsOptionSelected(true);
    };

    const clearItems = () => {
      setSelectedItem('');
      if (cleared) {
        cleared();
      }
    };

    const handleInputChange = (newValue: string) => {
      if (!isOptionSelected) {
        setInputValue(newValue);
      }
      setIsOptionSelected(false);
    };

    return (
      <Autocomplete
        ref={ref}
        noOptionsText="No options available"
        freeSolo
        selectOnFocus
        disabled={disabled}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        value={selectedItem}
        options={
          items ? items.sort((a, b) => -b.name.localeCompare(a.name)) : []
        }
        onInputChange={(event, newInputValue) => {
          handleInputChange(newInputValue);
        }}
        onChange={handleChange}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          if (typeof option === 'object' && option.name) {
            return option.name;
          }
          return '';
        }}
        sx={sx}
        renderInput={(params) => <TextField {...params} label={label} />}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;

          const isExisting = options.some(
            (option) => inputValue === option.name
          );

          if (inputValue !== '' && !isExisting && newText) {
            filtered.push({
              id: '',
              name: `Add "${inputValue}"`,
            });
          }

          return filtered.length > 0
            ? filtered
            : newText
            ? [
                {
                  id: '',
                  name: newText,
                },
              ]
            : [];
        }}
      />
    );
  }
);

export default GenericAutoComplete;
